import { View } from 'react-native'
import { useEffect, useState } from 'react'
import Animated, {
  interpolate,
  useAnimatedStyle,
  useSharedValue,
  withSpring,
} from 'react-native-reanimated'
import Icon, { IconName } from '@ui/elements/Icon'
import SectionRow from '@ui/elements/SectionRow'
import Highlights, { HighlightsLayers } from '@ui/effects/Highlights'
import { Collapsible } from '@ui/animated/Collapsible'
import Border from '@ui/effects/Border'
import { colors } from '@ui/colors'

import { borderRadius, borderWidthVariant } from './styles'
import { colorBorder } from '@ui/effects/Highlights/styles'

type Props = {
  title: string
  desc: string
  icon: IconName
  children?: React.ReactNode
  empty?: boolean
  content?: React.ReactNode
  highlightsLayers?: HighlightsLayers
  openSignal?: number
}

const defaultHighlightsLayers = {
  disabled: false,
  default: true,
  active: false,
  success: false,
  danger: false,
  attention: false,
} as const satisfies HighlightsLayers

export default function CollapsibleSectionRow({
  title,
  desc,
  icon,
  children,
  empty,
  content,
  ...props
}: Props) {
  const [open, setOpen] = useState(false)
  const onPress = () => setOpen((open) => !open)
  const animatedOpen = useSharedValue(0)

  useEffect(() => {
    if (props.openSignal) {
      setOpen(true)
    }
  }, [props.openSignal])

  const styleChevron = useAnimatedStyle(() => {
    const rotate = interpolate(animatedOpen.value, [0, 1], [0, 180])
    return {
      transform: [{ rotate: `${rotate}deg` }],
    }
  })

  useEffect(() => {
    animatedOpen.set(withSpring(open ? 1 : 0))
  }, [animatedOpen, open])

  const variant = empty ? 'danger' : 'default'
  const highlightsLayers = props.highlightsLayers || defaultHighlightsLayers

  return (
    <View
      className="relative overflow-hidden"
      style={{
        borderRadius,
      }}
    >
      <SectionRow
        isContained
        highlightsLayers={highlightsLayers}
        title={title}
        desc={desc}
        icon={icon}
        onPress={onPress}
        disabled={empty}
        variant={variant}
        actionComponent={
          !empty && (
            <Animated.View
              className="h-6 w-6 items-center justify-center"
              style={styleChevron}
            >
              <Icon name="chevron-down" size={24} color={colors.text} />
            </Animated.View>
          )
        }
      />
      {content && (
        <View className="relative">
          <Highlights
            variant={variant}
            layers={highlightsLayers}
            borderWidths={{ borderWidth: 0 }}
            borderRadiuses={{ borderRadius: 0 }}
          />
          {content}
        </View>
      )}
      {!empty && (
        <Collapsible open={open} fullWidth lazy>
          <View className="relative flex-1 flex-col border-t border-black/10">
            <Highlights
              variant={variant}
              layers={highlightsLayers}
              borderWidths={{ borderWidth: 0 }}
              borderRadiuses={{ borderRadius: 0 }}
            />
            {children}
          </View>
        </Collapsible>
      )}
      <Border
        style={{
          borderColor: colorBorder[variant],
          borderRadius,
          borderWidth: borderWidthVariant[variant],
        }}
      />
    </View>
  )
}
